/**
 * VRG-679 — Outreach: Traction channel #13 (Sales) as Vera operates it. An outreach CAMPAIGN is
 * either email outreach or an in-person city trip (a "field visit" — a campaign TYPE, never a
 * sidebar label, and never a source-channel value: everything a campaign produces is recorded
 * as `outreach`, VRG-675). Each campaign lists target brands with a per-brand status.
 *
 * Text + CHECK constraints in migration 0137, not Postgres enums, for the same reason as
 * source channels (0136). `outreach.guard.spec.ts` keeps these lists and the CHECKs in step.
 */
export const OUTREACH_CAMPAIGN_TYPES = ['email', 'in_person'] as const;
export type OutreachCampaignType = (typeof OUTREACH_CAMPAIGN_TYPES)[number];

export const OUTREACH_CAMPAIGN_TYPE_LABELS: Readonly<Record<OutreachCampaignType, string>> = {
  email: 'Email outreach',
  in_person: 'Field visit',
};

/**
 * Per-brand status, in the order a target moves through it. `declined` is terminal and can follow
 * any of the others. VRG-684 (Spencer, 2026-10-08) added `meeting_held`, `pilot` and
 * `loi_signed` — the outcomes the field-visits playbook asks to be measured — in migration 0139.
 */
export const OUTREACH_TARGET_STATUSES = [
  'to_contact',
  'sent',
  'replied',
  'meeting_booked',
  'meeting_held',
  'pilot',
  'loi_signed',
  'declined',
] as const;
export type OutreachTargetStatus = (typeof OUTREACH_TARGET_STATUSES)[number];

export const OUTREACH_TARGET_STATUS_LABELS: Readonly<Record<OutreachTargetStatus, string>> = {
  to_contact: 'To contact',
  sent: 'Sent',
  replied: 'Replied',
  meeting_booked: 'Meeting booked',
  meeting_held: 'Meeting held',
  pilot: 'Pilot',
  loi_signed: 'LOI signed',
  declined: 'Declined',
};

export const isOutreachCampaignType = (v: unknown): v is OutreachCampaignType =>
  typeof v === 'string' && (OUTREACH_CAMPAIGN_TYPES as readonly string[]).includes(v);

export const isOutreachTargetStatus = (v: unknown): v is OutreachTargetStatus =>
  typeof v === 'string' && (OUTREACH_TARGET_STATUSES as readonly string[]).includes(v);
